<template>
    <Dialog
        :value="showDialog"
        :header-text="title"
        :width="560"
        persistent
        @close_modal="$emit('close')"
    >
        <v-container v-if="form_data" class="px-0">
            <v-row>
                <v-col cols="12">
                    <v-text-field
                        v-model="form_data.code"
                        :label="$t('Code')"
                        :error-messages="form_data.code_error"
                        outlined
                    ></v-text-field>
                </v-col>
                <v-col cols="6">
                    <v-text-field
                        v-model="form_data.amount_f"
                        :label="$t('Amount')"
                        :error-messages="form_data.amount_error"
                        outlined
                        @input="numberFieldChanged($event, 'amount')"
                    ></v-text-field>
                </v-col>
                <v-col cols="6">
                    <v-select
                        v-model="form_data.type"
                        :items="types"
                        item-text="title"
                        item-value="name"
                        :label="$t('Unit')"
                        :error-messages="form_data.type_error"
                        outlined
                    ></v-select>
                </v-col>
                <v-col cols="6">
                    <v-select
                        v-model="form_data.period_months"
                        :items="periods"
                        :label="$t('Period (months)')"
                        :error-messages="form_data.period_months_error"
                        outlined
                    ></v-select>
                </v-col>
                <v-col cols="6">
                    <v-menu
                        v-model="date_picker.valid_until"
                        offset-y
                        :close-on-content-click="false"
                        :nudge-top="18"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :label="$t('Valid until')"
                                readonly
                                :value="form_data.valid_until | format_date"
                                :error-messages="form_data.valid_until_error"
                                outlined
                                append-icon="mdi-calendar"
                                v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="form_data.valid_until"
                            min="1900-01-01"
                            color="primary"
                            no-title
                            :locale="locale"
                            :first-day-of-week="firstDayOfWeek"
                            @input="menu_open = false"
                        ></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="6">
                    <v-menu
                        v-model="date_picker.account_created_from"
                        offset-y
                        :close-on-content-click="false"
                        :nudge-top="18"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :label="$t('Account created from')"
                                readonly
                                :value="form_data.account_created_from | format_date"
                                :error-messages="form_data.account_created_from_error"
                                outlined
                                append-icon="mdi-calendar"
                                v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="form_data.account_created_from"
                            min="1900-01-01"
                            color="primary"
                            no-title
                            :locale="locale"
                            :first-day-of-week="firstDayOfWeek"
                            @input="menu_open = false"
                        ></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="6">
                    <v-menu
                        v-model="date_picker.account_created_until"
                        offset-y
                        :close-on-content-click="false"
                        :nudge-top="18"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :label="$t('Account created until')"
                                readonly
                                :value="form_data.account_created_until | format_date"
                                :error-messages="form_data.account_created_until_error"
                                outlined
                                append-icon="mdi-calendar"
                                v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="form_data.account_created_until"
                            min="1900-01-01"
                            color="primary"
                            no-title
                            :locale="locale"
                            :first-day-of-week="firstDayOfWeek"
                            @input="menu_open = false"
                        ></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="6">
                    <v-text-field
                        v-model="form_data.usage_limit_f"
                        :label="$t('Usage limit')"
                        :error-messages="form_data.usage_limit_error"
                        outlined
                        @input="numberFieldChanged($event, 'usage_limit')"
                    ></v-text-field>
                </v-col>
                <v-col cols="6">
                    <v-text-field
                        v-model="form_data.used_count_f"
                        :label="$t('Used count')"
                        :error-messages="form_data.used_count_error"
                        disabled
                        outlined
                    ></v-text-field>
                </v-col>
                <v-col cols="6">
                    <v-text-field
                        v-model="form_data.free_days_f"
                        :label="$t('Free days')"
                        :error-messages="form_data.free_days_error"
                        outlined
                        @input="numberFieldChanged($event, 'free_days')"
                    ></v-text-field>
                </v-col>
            </v-row>
        </v-container>
        <template slot="footer">
            <v-btn v-if="discountId" icon @click="deleteVoucher">
                <v-icon>mdi-delete</v-icon>
            </v-btn>
            <v-spacer/>
            <v-btn text @click="$emit('close')">
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn class="primary inverted--text" :loading="loading" @click="saveVoucher">
                {{ $t("Save") }}
            </v-btn>
        </template>
    </Dialog>
</template>

<script>
import Vue from "vue";
import {ref, computed} from "@vue/composition-api";
import {$t} from "@/i18n";
import Dialog from "../../components/Dialog.vue";
import AppModule from "@/store/modules/app";
import SubscriptionModule from "@/store/modules/subscription";
import {cleanNumber} from "@/plugins/formatting";
import DialogModule from "@/store/modules/dialog";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "AdminVoucherDialog",
    components: {
        Dialog
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true
        },
        discountId: {
            type: Number,
            required: true
        }
    },
    setup(props, ctx) {
        const {formatting, handleErrorResponse, clearValidationErrors} = AppModule();
        const {adminFetchVoucher, adminSaveVoucher, adminDeleteVoucher} = SubscriptionModule();
        const {openConfirmDialog, setConfirmOptions} = DialogModule();

        const form_data = ref(null);
        const loading = ref(false);
        const types = [
            {name: "percent", title: $t("Percent")},
            {name: "sum", title: $t("Sum")},
        ];
        const periods = (() => {
            let ret = [];
            for (let i = 1; i <= 60; i++) {
                ret.push(i);
            }
            return ret;
        })();
        const date_picker = ref({
            valid_until: false,
            account_created_from: false,
            account_created_until: false
        });

        const title = computed(() => {
            return props.discountId === 0 ? $t("Create voucher") : $t("Edit voucher")
        });
        const locale = computed(function () {
            return formatting.value.culture;
        });
        const firstDayOfWeek = computed(function() {
            return formatting.value.first_week_day;
        });

        const loadData = () => {
            if (props.discountId === 0) {
                let today = new Date();
                prepareFormData({
                    id: 0,
                    type: "sum",
                    period_months: 1,
                    valid_until: today.calcAddMonths(1).formatSysDate(),
                    account_created_from: today.formatSysDate(),
                    account_created_until: today.calcAddMonths(1).formatSysDate(),
                    usage_limit: 1,
                    used_count: 0,
                    free_days: 0
                })
                return;
            }

            adminFetchVoucher(props.discountId)
                .then(resp => prepareFormData(resp))
                .catch(ex => errorDialog.showError(ex));
        };
        const prepareFormData = data => {
            form_data.value = Vue.prepare_form_data(data, {
                code: "text",
                amount: "number",
                type: "choice_single",
                period_months: "integer",
                valid_until: "date",
                account_created_from: "date",
                account_created_until: "date",
                usage_limit: "integer",
                used_count: "integer",
                free_days: "integer"
            })
        };
        const saveVoucher = () => {
            loading.value = true;
            clearValidationErrors(form_data.value);

            let data = Vue.clean_and_clone_object(form_data.value);

            adminSaveVoucher(data)
                .then(resp => {
                    loading.value = false;
                    ctx.emit("updated", resp);
                })
                .catch(ex => {
                    loading.value = false;
                    handleErrorResponse(form_data.value, ex);
                });
        };
        const deleteVoucher = () => {
            setConfirmOptions({
                title: $t("Are you sure to delete this voucher?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => {
                    adminDeleteVoucher(props.discountId)
                        .then(() => ctx.emit("deleted", props.discountId))
                        .catch(ex => errorDialog.showError(form_data.value, ex));
                }
            });
            openConfirmDialog();
        };
        const numberFieldChanged = (data, field) => {
            let num_str = cleanNumber(data);
            if (isNaN(num_str)) {
                form_data.value[field + "_error"] = $t("Must be a number");
                form_data.value[field] = null;
            } else {
                form_data.value[field + "_error"] = null;
                form_data.value[field] = parseFloat(num_str);
            }
        };

        loadData();

        return {
            title,
            form_data,
            loading,
            types,
            periods,
            date_picker,
            locale,
            saveVoucher,
            deleteVoucher,
            numberFieldChanged,
            firstDayOfWeek
        };
    }
};
</script>